import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Badge, Spinner, Alert } from 'reactstrap';
import { H5, H6 } from '../../../AbstractElements';
import { loadingAllAdmin, getAdminId, isAuthenticated } from '../../../api/admin';
import { Users, UserCheck, UserX, Shield } from 'react-feather';

const AdminStats = () => {
  const [adminData, setAdminData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentAdminId, setCurrentAdminId] = useState(null);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Kiểm tra authentication
      if (!isAuthenticated()) {
        throw new Error('Chưa đăng nhập');
      }

      // Lấy danh sách tất cả admin
      const admins = await loadingAllAdmin();
      setAdminData(admins);

      // Lấy admin ID hiện tại
      const adminId = getAdminId();
      setCurrentAdminId(adminId);

    } catch (err) {
      console.error('❌ Error loading admin data:', err);
      setError(err.message || 'Không thể tải dữ liệu admin');
    } finally {
      setLoading(false);
    }
  };

  // Tính toán thống kê
  const getStats = () => {
    if (!adminData || adminData.length === 0) {
      return {
        totalAdmins: 0,
        superAdmins: 0,
        normalAdmins: 0,
        activeAdmins: 0
      };
    }

    const totalAdmins = adminData.length;
    const superAdmins = adminData.filter(admin => admin.role_code === 'R1').length;
    const normalAdmins = adminData.filter(admin => admin.role_code === 'R2').length;
    const activeAdmins = adminData.filter(admin => admin.isActive !== false).length;

    return {
      totalAdmins,
      superAdmins,
      normalAdmins,
      activeAdmins
    };
  };

  const stats = getStats();

  // Dữ liệu cho các card thống kê
  const statsData = [
    {
      title: 'Tổng Admin',
      value: stats.totalAdmins,
      color: 'primary',
      icon: Users,
      description: 'Tổng số admin trong hệ thống'
    },
    {
      title: 'Super Admin',
      value: stats.superAdmins,
      color: 'success',
      icon: Shield,
      description: 'Admin có quyền cao nhất'
    },
    {
      title: 'Admin Thường',
      value: stats.normalAdmins,
      color: 'info',
      icon: UserCheck,
      description: 'Admin có quyền hạn chế'
    },
    {
      title: 'Admin Hoạt Động',
      value: stats.activeAdmins,
      color: 'warning',
      icon: UserX,
      description: 'Admin đang hoạt động'
    }
  ];

  if (loading) {
    return (
      <Col xxl="12" className="box-col-12">
        <Card>
          <CardBody className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <div className="text-center">
              <Spinner color="primary" />
              <p className="mt-2 mb-0">Đang tải dữ liệu admin...</p>
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  }

  if (error) {
    return (
      <Col xxl="12" className="box-col-12">
        <Card>
          <CardBody>
            <Alert color="danger">
              <strong>Lỗi:</strong> {error}
            </Alert>
          </CardBody>
        </Card>
      </Col>
    );
  }

  return (
    <Col xxl="12" className="box-col-12">
      <Card>
        <CardHeader className="card-no-border">
          <H5>Thống Kê Admin</H5>
          {currentAdminId && (
            <small className="text-muted">
              Admin hiện tại: {adminData.find(admin => admin.id.toString() === currentAdminId)?.name || 'Unknown'}
            </small>
          )}
        </CardHeader>
        <CardBody>
          <Row className="g-3">
            {statsData.map((item, index) => {
              const Icon = item.icon;
              return (
                <Col key={index} xl="3" md="6" sm="6" className="box-col-6">
                  <Card className="stat-widget">
                    <CardBody>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex flex-column justify-content-center" style={{ minWidth: 0, flex: 1 }}>
                          <span className="f-light f-w-500 f-14 d-block mb-2">{item.title}</span>
                          <H6 attrH6={{ className: 'mb-0' }}>
                            <Badge color={item.color} className="badge-lg">
                              {item.value}
                            </Badge>
                          </H6>
                          <small className="text-muted mt-1">{item.description}</small>
                        </div>
                        <div 
                          className={`bg-${item.color} rounded d-flex align-items-center justify-content-center`} 
                          style={{ width: '48px', height: '48px', flexShrink: 0 }}
                        >
                          <Icon size={22} className="text-white" />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
          
          {/* Hiển thị danh sách admin gần đây */}
          {adminData.length > 0 && (
            <div className="mt-4">
              <H6>Danh Sách Admin Gần Đây</H6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Tên</th>
                      <th>Username</th>
                      <th>Role</th>
                      <th>Trạng Thái</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminData.slice(0, 5).map((admin, index) => (
                      <tr key={admin.id}>
                        <td>{admin.id}</td>
                        <td>{admin.name}</td>
                        <td>{admin.username}</td>
                        <td>
                          <Badge color={admin.role_code === 'R1' ? 'success' : 'info'}>
                            {admin.role_code === 'R1' ? 'Super Admin' : 'Admin'}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={admin.isActive !== false ? 'success' : 'danger'}>
                            {admin.isActive !== false ? 'Hoạt động' : 'Không hoạt động'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default AdminStats;
